import React, { useEffect, useMemo, useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';

import './coachingGlidePath.scss';
import { CaluculateMetricValue, metricValueFormat } from '../../../utils/metricValueFormat';
import { monthNames } from '../../../utils/typeConstants';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    Filler
);

const MULTI_GOAL_RULE_ID = 2;

// tier_level values in each session's `targets` array.
const MEETS_TIER_LEVEL = 1;
const EXCEEDS_TIER_LEVEL = 2;
const THRESHOLD_TIER_LEVEL = 3;

interface MetricTargetTier {
    target_tier_id: number;
    target_tier_name: string;
    tier_level: number;
    target: number | null;
}

/** Normalizes a session's `targets` field to an array regardless of
 *  whether it arrives as the real MetricTargetTier[] (expected, once the
 *  backend deserializes it) or as a raw JSON string (seen directly off the
 *  SP's row output) — defensive so this keeps working either way rather
 *  than assuming one specific shape. */
function getSessionTiers(raw: unknown): MetricTargetTier[] {
    if (Array.isArray(raw)) return raw as MetricTargetTier[];
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

function getTierTarget(targets: unknown, tierLevel: number): number | null {
    return getSessionTiers(targets).find((t) => t.tier_level === tierLevel)?.target ?? null;
}

function getTierName(targets: unknown, tierLevel: number): string | null {
    return getSessionTiers(targets).find((t) => t.tier_level === tierLevel)?.target_tier_name ?? null;
}

// "meets"/"exceeds" — formerly "base"/"stretch".
type GoalKey = 'target' | 'exceeds' | 'meets' | 'threshold';

interface Goal {
    key: GoalKey;
    label: string;
    lineColor: string;
    labelColor: string;
    dash: number[];
    value: number;
    displayValue: string;
}

// Colors are separated per goal so lines/labels stay visually distinct.
// Tweak freely — purely presentational. `dash` is the line-dash pattern for
// that goal's reference line — Meets/Exceeds now use different patterns
// ([5,5] vs [2,3]) so they're distinguishable by shape as well as color.
// NOTE: keys are `lineColor`/`labelColor` (not `line`/`label`) specifically
// so spreading these into a goal literal never collides with that
// literal's own `label` (display text) or `key` fields.
const GOAL_COLORS: Record<GoalKey, { lineColor: string; labelColor: string; dash: number[] }> = {
    // Updated per request: Target line + legend text now #2AA154 (was #B14F2F).
    target: { lineColor: '#2AA154', labelColor: '#2AA154', dash: [5, 5] },
    exceeds: { lineColor: '#00B6B2', labelColor: '#00B6B2', dash: [2, 3] },
    meets: { lineColor: '#089886', labelColor: '#089886', dash: [5, 5] },
    threshold: { lineColor: '#D97706', labelColor: '#D97706', dash: [5, 5] },
};

/* ────────────────────────────────────────────────────────────────────────
 * THREE INDEPENDENT COLOR SYSTEMS (confirmed from Figma spec — cosmetic
 * only, no change to what data is plotted or how goals/thresholds are
 * computed)
 * ────────────────────────────────────────────────────────────────────────
 * 1. SYSTEM_COLORS — fixed, NOT index-dependent. Governs every regular
 *    trend point's dot + value-label text, purely by which region its
 *    value falls into (same 3 colors regardless of which metric this
 *    chart is for), plus the override marker's fixed color.
 * 2. CURRENT_POINT_COLORS — index-dependent. The Current (most recent /
 *    synthetic result) point's dot + text NEVER follows region coloring
 *    at all — it always uses this dedicated per-metric-index color.
 * 3. METRIC_EXCEEDS_LINE_COLORS / METRIC_MEETS_LINE_COLORS — index-
 *    dependent. Color the flat dashed Meets/Exceeds REFERENCE LINES only
 *    (same rendering as the Target line) — not any data point.
 * These are deliberately a separate, dedicated palette for this
 * component rather than reusing TieredMetricColors from GradientDefs.tsx
 * — the confirmed hex values here don't match that file's values
 * (e.g. index-0 Exceeds here is FF9933 vs. TieredMetricColors[0].light
 * E26A43), so unifying them would change values on either this chart or
 * the tiered gauge elsewhere. Revisit if a single shared source is wanted
 * later.
 * ──────────────────────────────────────────────────────────────────────── */
const SYSTEM_COLORS = {
    exceedsText: '#2AA154',
    meetsText: '#5279DD',
    belowMeetsText: '#E23636',
    overrideText: '#A1025A',
};

const CURRENT_POINT_COLORS = ['#F15523', '#00B6B2', '#1A5AA5', '#701AC0', '#A71AA0', '#C11E52'];

const METRIC_EXCEEDS_LINE_COLORS = ['#FF9933', '#0CD9BF', '#499DFF', '#AC53FF', '#EB6BE5', '#FF568B'];
const METRIC_MEETS_LINE_COLORS = ['#E26A43', '#00B6B2', '#2A78D3', '#8913CD', '#C848C2', '#E83683'];

// Fixed — the CASE 1 "projected line" (start value -> target, before any
// real session data exists) always renders in this color, regardless of
// metric index or region. Region-based coloring only makes sense once
// there's a real trend to color by, which this line isn't.
const PROJECTION_LINE_COLOR = '#089886';

/** Picks palette[index % palette.length]. If `index` isn't supplied at
 *  all, it's treated as 0 — not a separate fixed fallback color, just
 *  "assume metric index 0" (palette[0]'s own color, whatever that is per
 *  palette). */
function pickByIndex(palette: string[], index: number | undefined): string {
    const effectiveIndex = index ?? 0;
    return palette[effectiveIndex % palette.length] ?? palette[0];
}

const CoachingGlidePath: React.FC<{
    CAPSession: any;
    pageType: string;
    // Optional — existing call sites that don't pass this are treated as
    // index 0 (see pickByIndex above), not a separate fixed default.
    index?: number;
}> = ({ CAPSession, pageType, index }) => {
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstanceRef = useRef<ChartJS<'line'> | null>(null);

    const lastSession = CAPSession?.sessions?.reduce((max: any, session: any) =>
        session.sessionNumber > max.sessionNumber ? session : max
    );

    const isMultiGoal = CAPSession?.scorecardCalculationRuleId === MULTI_GOAL_RULE_ID;

    /* Goals are computed here (not inside the chart useEffect) so the same
     * list drives both what's plotted on the canvas AND the legend below —
     * one source of truth, impossible for them to drift out of sync.
     *
     * Meets/Exceeds dashed-line colors always come from the per-index
     * palettes (index defaults to 0 when not supplied — see pickByIndex).
     * Target/Threshold have no per-index equivalent, so those always use
     * GOAL_COLORS regardless of index. Dash pattern (goal.dash) always
     * comes from GOAL_COLORS regardless of index — it's not part of the
     * per-index palettes, just a fixed shape distinguishing Meets from
     * Exceeds. */
    const resolveGoalColors = (colorKey: GoalKey): { lineColor: string; labelColor: string; dash: number[] } => {
        if (colorKey === 'meets') {
            const c = pickByIndex(METRIC_MEETS_LINE_COLORS, index);
            return { lineColor: c, labelColor: c, dash: GOAL_COLORS.meets.dash };
        }
        if (colorKey === 'exceeds') {
            const c = pickByIndex(METRIC_EXCEEDS_LINE_COLORS, index);
            return { lineColor: c, labelColor: c, dash: GOAL_COLORS.exceeds.dash };
        }
        return GOAL_COLORS[colorKey];
    };

    const goals: Goal[] = useMemo(() => {
        if (!isMultiGoal) {
            const sessionZero = CAPSession?.sessions?.[0];
            const rawValue = sessionZero?.metricTargetValue;
            if (rawValue == null) return [];
            return [{
                key: 'target',
                label: 'Target',
                ...resolveGoalColors('target'),
                value: CaluculateMetricValue(rawValue, CAPSession?.metricTypeName),
                displayValue: metricValueFormat(rawValue, CAPSession?.metricTypeName),
            }];
        }

        const buildFlatGoal = (colorKey: GoalKey, tierLevel: number, fallbackLabel: string): Goal | null => {
            const rawValue = getTierTarget(lastSession?.targets, tierLevel);
            if (rawValue == null) return null; // this tier not configured — omit entirely
            const label = getTierName(lastSession?.targets, tierLevel) ?? fallbackLabel;
            return {
                key: colorKey,
                label,
                ...resolveGoalColors(colorKey),
                value: CaluculateMetricValue(rawValue, CAPSession?.metricTypeName),
                displayValue: metricValueFormat(rawValue, CAPSession?.metricTypeName),
            };
        };

        return [
            buildFlatGoal('exceeds', EXCEEDS_TIER_LEVEL, 'Exceeds'),
            buildFlatGoal('meets', MEETS_TIER_LEVEL, 'Meets'),
            buildFlatGoal('threshold', THRESHOLD_TIER_LEVEL, 'Threshold'),
        ].filter((g): g is Goal => g != null);
    }, [CAPSession, isMultiGoal, lastSession, index]);

    // Current point's dedicated per-index color — never follows region
    // coloring (confirmed: Current always uses this palette, regardless of
    // where its value falls relative to Meets/Exceeds).
    const currentPointColor = pickByIndex(CURRENT_POINT_COLORS, index);

    const meetsGoal = goals.find((g) => g.key === 'meets');
    const exceedsGoal = goals.find((g) => g.key === 'exceeds');
    // Only present in rule-1 mode (goals only contains 'target' then) — used
    // below so a regular point above the Target line also gets colored
    // #2AA154, matching the updated Target line/legend color.
    const targetGoal = goals.find((g) => g.key === 'target');

    /** Resolves the color a regular (non-override, non-Current) data point
     *  should render in — its dot AND its value-label text both use this,
     *  so they can never disagree. Fixed SYSTEM_COLORS, NOT index-dependent:
     *  the same 3 colors regardless of which metric this chart is for.
     *  Below Meets (or no Meets configured, or value is null/unmeasured)
     *  -> belowMeetsText. At/above Meets -> meetsText. At/above Exceeds
     *  -> exceedsText. Rule-1 addition: at/above Target -> #2AA154 (same
     *  color as the Target line/legend); everything else in rule 1
     *  unchanged (still belowMeetsText). */
    const getRegionColor = (value: number | null | undefined): string => {
        if (value == null) return SYSTEM_COLORS.belowMeetsText;
        if (exceedsGoal && value >= exceedsGoal.value) return SYSTEM_COLORS.exceedsText;
        if (meetsGoal && value >= meetsGoal.value) return SYSTEM_COLORS.meetsText;
        if (targetGoal && value >= targetGoal.value) return '#2AA154';
        return SYSTEM_COLORS.belowMeetsText;
    };

    useEffect(() => {
        if (!chartRef.current) return undefined;

        const ctx = chartRef.current.getContext('2d');
        if (!ctx) return undefined;

        // Generate dates and data based on case
        let labels: string[];
        let trendData: (number | string | null)[];
        let startIndex: number;
        let endIndex: number;
        let shouldAddResultPoint = false;
        // True only for CASE 1 below (no real session data yet — just a
        // flat projected line from start value to target). Used further
        // down to force that line's color to PROJECTION_LINE_COLOR instead
        // of the usual region-based coloring, which only makes sense once
        // there's a real trend to color by region.
        const isProjectedLine = (CAPSession?.sessions && CAPSession?.sessions.length > 0);
        if (CAPSession?.sessions && CAPSession?.sessions.length > 0) {
            // CASE 2: We have actual data points
            const sessionLabels = CAPSession.sessions.map((point: any) => {
                const date = new Date(point.sessionStartDate);
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            });

            const sessionTrendData = CAPSession.sessions.map((point: any) => {
                return point.metricCurrentValue !== null && point.metricCurrentValue !== undefined
                    ? CaluculateMetricValue(point.metricCurrentValue, CAPSession.metricTypeName)
                    : null;
            });

            // ── Anchor date: today if in progress, coachingCompleteDate if completed ──
            const getAnchorDate = () => {
                if (CAPSession?.coachingActionPlanStatusId === 3 && CAPSession?.coachingCompleteDate) {
                    const [datePart] = CAPSession.coachingCompleteDate.split(' ');
                    const [month, day, year] = datePart.split('/');
                    return new Date(Number(year), Number(month) - 1, Number(day));
                }
                if (CAPSession?.coachingActionPlanStatusId === 2) {
                    return new Date();
                }
                return null;
            };

            const anchorDate = getAnchorDate();

            const lastSessionDate = lastSession?.sessionStartDate
                ? (() => {
                    const [datePart] = lastSession.sessionStartDate.split(' ');
                    const [month, day, year] = datePart.split('/');
                    return new Date(Number(year), Number(month) - 1, Number(day));
                })()
                : null;

            const today = new Date();
            const isLastSessionToday = lastSessionDate
                && lastSessionDate.getFullYear() === today.getFullYear()
                && lastSessionDate.getMonth() === today.getMonth()
                && lastSessionDate.getDate() === today.getDate();

            const anchorLabel = anchorDate
                ? anchorDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : null;

            const lastSessionLabel = sessionLabels[sessionLabels.length - 1];
            shouldAddResultPoint = !isLastSessionToday
                && anchorLabel !== null
                && anchorLabel !== lastSessionLabel;

            if (shouldAddResultPoint) {
                labels = [...sessionLabels, anchorLabel];
                const resultValue = lastSession?.metricResultValue != null
                    ? CaluculateMetricValue(lastSession.metricResultValue, CAPSession.metricTypeName)
                    : null;
                trendData = [...sessionTrendData, resultValue];
            } else {
                labels = sessionLabels;
                trendData = sessionTrendData;
            }

            startIndex = 0;
            endIndex = labels.length - 1;
        } else {
            // CASE 1: Only start value - generate projected line
            const generateDates = () => {
                const dates = [];
                const startDate = CAPSession?.sessions[0].sessionStartDate;
                const endDate = lastSession?.sessionStartDate;
                if (startDate && endDate) {
                    const totalDays = Math.floor(
                        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
                    );
                    const interval = Math.floor(totalDays / 12);

                    for (let i = 0; i <= 12; i++) {
                        const currentDate = new Date(startDate);
                        currentDate.setDate(startDate.getDate() + i * interval);
                        dates.push(
                            currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        );
                    }
                }
                return dates;
            };

            labels = generateDates();
            startIndex = Math.floor(labels.length / 3);
            endIndex = labels.length - 1;

            // Create projected line from startValue to targetValue
            const line: (number | null)[] = new Array(labels.length).fill(null);
            line[startIndex] = CaluculateMetricValue(
                CAPSession?.metricStartValue,
                CAPSession?.metricTypeName
            );
            line[endIndex] = CaluculateMetricValue(CAPSession?.sessions[0]?.metricTargetValue, CAPSession?.metricTypeName);

            trendData = line;
        }

        // Generic replacement for the old single-purpose createTargetLine()
        const createGoalLine = (goalValue: number) => new Array(labels.length).fill(goalValue);

        // Custom plugin to draw labels
        const labelsPlugin = {
            id: 'customLabels',
            afterDatasetsDraw(chart: any) {
                const ctx = chart.ctx;
                const chartArea = chart.chartArea;
                const yScale = chart.scales.y;

                // ── Draw each goal's horizontal dashed reference line ────────────────
                // Dash pattern now comes from goal.dash (Meets [5,5],
                // Exceeds [2,3], Target/Threshold [5,5]) instead of a single
                // hardcoded [4, 2] for every goal — matches the underlying
                // Chart.js dataset's own borderDash below, so the two
                // renderings of the same line never disagree on dash shape.
                goals.forEach((goal) => {
                    const lineY = yScale.getPixelForValue(goal.value);

                    ctx.save();
                    ctx.beginPath();
                    ctx.setLineDash(goal.dash);
                    ctx.lineWidth = 1.5;
                    ctx.strokeStyle = goal.lineColor;
                    ctx.moveTo(chartArea.left, lineY);
                    ctx.lineTo(chartArea.right, lineY);
                    ctx.stroke();
                    ctx.setLineDash([]); // reset dash so nothing else is affected
                    ctx.restore();
                });

                // Draw values on trend line data points
                // (dataset index looked up by label instead of hardcoded `1`,
                // since the number of goal-line datasets in front of it now varies)
                const metricDatasetIndex = chart.data.datasets.findIndex((d: any) => d.label === 'Metric value');
                const meta = chart.getDatasetMeta(metricDatasetIndex);

                meta.data.forEach((dataPoint: any, dataIndex: number) => {
                    if (!dataPoint) return;

                    const value = trendData[dataIndex];
                    const isResultPoint = shouldAddResultPoint && dataIndex === trendData.length - 1;
                    const session = isResultPoint ? lastSession : CAPSession?.sessions?.[dataIndex];
                    const hasSessionOverride = isResultPoint
                        ? lastSession?.metricFinalValueOverride != null       // use metricFinalValueOverride for result point
                        : session?.metricCurrentValueOverride != null;

                    const overrideValue = isResultPoint
                        ? lastSession?.metricFinalValueOverride
                        : session?.metricCurrentValueOverride;

                    if (value === null && !hasSessionOverride) return;

                    const x = dataPoint.x;

                    let overrideY: number | null = null;
                    if (hasSessionOverride && overrideValue != null) {
                        const calculatedOverrideVal = CaluculateMetricValue(overrideValue, CAPSession?.metricTypeName);
                        overrideY = yScale.getPixelForValue(calculatedOverrideVal);
                    }

                    // Current point always uses its own dedicated per-index
                    // color (confirmed: never follows region coloring).
                    // Every other point follows the fixed region rule.
                    const pointColor = isResultPoint
                        ? currentPointColor
                        : getRegionColor(typeof value === 'number' ? value : null);

                    const OVERLAP_THRESHOLD = 40;
                    ctx.save();

                    // ── Main value label ───────────────────────────────────────────
                    if (value !== null && value !== undefined) {
                        const y = dataPoint.y;
                        const decimalValue = parseFloat(value?.toString())?.toFixed(2);
                        const displayText = CAPSession?.metricTypeName === 'Percentage'
                            ? value + '%'
                            : decimalValue;

                        ctx.textAlign = 'left';
                        ctx.fillStyle = pointColor;
                        ctx.font = 'bold 11px Inter, sans-serif';

                        const mainIsLower = overrideY !== null
                            && y > overrideY
                            && (y - overrideY) < OVERLAP_THRESHOLD;

                        if (mainIsLower) {
                            ctx.fillText(displayText, x - 5, y + 18);
                            if (dataIndex === startIndex) ctx.fillText('Start', x - 5, y + 30);
                            if (isResultPoint) ctx.fillText('Current', x - 5, y + 30);
                        } else {
                            ctx.fillText(displayText, x - 5, y - 10);
                            if (dataIndex === startIndex) ctx.fillText('Start', x - 5, y - 22);
                            if (isResultPoint) ctx.fillText('Current', x - 5, y - 22);
                        }
                    } else {
                        const nullLabelY = chartArea.bottom + 14;
                        ctx.textAlign = 'left';
                        ctx.fillStyle = pointColor;
                        ctx.font = 'bold 12px Inter, sans-serif';
                        ctx.fillText('--', x - 5, nullLabelY - 5);
                        if (dataIndex === startIndex) {
                            ctx.font = 'bold 10px Inter, sans-serif';
                            ctx.fillText('Start', x - 5, nullLabelY - 12);
                        }
                        if (isResultPoint) {
                            ctx.font = 'bold 10px Inter, sans-serif';
                            ctx.fillText('Current', x - 5, nullLabelY - 12);
                        }
                    }

                    // ── Override dot + labels (requirement #4: always
                    // SYSTEM_COLORS.overrideText, regardless of which region
                    // the override value itself would otherwise fall into,
                    // and regardless of metric index) ──
                    if (hasSessionOverride && overrideY !== null && overrideValue != null) {
                        const calculatedOverrideVal = CaluculateMetricValue(overrideValue, CAPSession?.metricTypeName);
                        const overrideDisplay = CAPSession?.metricTypeName === 'Percentage'
                            ? calculatedOverrideVal + '%'
                            : parseFloat(calculatedOverrideVal?.toString()).toFixed(2);

                        ctx.beginPath();
                        ctx.arc(x, overrideY, 6, 0, Math.PI * 2);
                        ctx.fillStyle = SYSTEM_COLORS.overrideText;
                        ctx.fill();
                        ctx.strokeStyle = '#fff';
                        ctx.lineWidth = 2;
                        ctx.stroke();

                        const mainDotY = value !== null ? dataPoint.y : null;
                        const overrideIsLower = mainDotY !== null
                            && overrideY > mainDotY
                            && (overrideY - mainDotY) < OVERLAP_THRESHOLD;

                        ctx.fillStyle = SYSTEM_COLORS.overrideText;
                        ctx.textAlign = 'left';

                        if (overrideIsLower) {
                            ctx.font = 'bold 11px Inter, sans-serif';
                            ctx.fillText('Override Data', x - 5, overrideY + 18);
                            ctx.fillText(overrideDisplay, x - 5, overrideY + 30);
                        } else {
                            ctx.font = 'bold 11px Inter, sans-serif';
                            ctx.fillText(overrideDisplay, x - 5, overrideY - 12);
                            ctx.fillText('Override Data', x - 5, overrideY - 24);
                        }
                    }

                    ctx.restore();
                });
            },
        };

        // Destroy any existing chart before creating new one
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
            chartInstanceRef.current = null;
        }
        if (labels.length <= 7) {
            // Set canvas width based on number of data points (200px per point, min 400px)
            const POINT_WIDTH = 200;
            const canvasWidth = Math.max(labels.length * POINT_WIDTH, 400);
            chartRef.current.style.width = `${canvasWidth}px`;
            chartRef.current.width = canvasWidth;

            // Add these two lines so the canvas height matches the container
            chartRef.current.style.height = '180px';
            chartRef.current.height = 180;
        }

        // Dash pattern per goal now comes from goal.dash (Meets [5,5],
        // Exceeds [2,3], Target/Threshold [5,5]) instead of one hardcoded
        // [5, 5] for every goal.
        const goalDatasets = goals.map((goal) => ({
            label: goal.label,
            data: createGoalLine(goal.value),
            borderColor: goal.lineColor,
            backgroundColor: 'transparent',
            borderDash: goal.dash,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
        }));

        // Extracted into its own array (rather than computed inline twice)
        // since it now drives BOTH the dot colors (pointBackgroundColor)
        // AND the connecting line's per-segment color below — single source
        // of truth, same as meetsGoal/exceedsGoal already are for goal
        // colors.
        const pointColors = trendData.map((value: any, i: number) => {
            const isCurrentPoint = i === trendData.length - 1 && shouldAddResultPoint;
            if (isCurrentPoint) return currentPointColor;
            return getRegionColor(typeof value === 'number' ? value : null);
        });

        // pointBackgroundColor/segment.borderColor both follow the same
        // rule as the labelsPlugin's own text labels above (fixed
        // SYSTEM_COLORS for regular points, currentPointColor for Current)
        // — so the dot, its value-label text, and the line segment leading
        // INTO it can never disagree on color. Except for the CASE 1
        // projection line, which is fixed to PROJECTION_LINE_COLOR
        // regardless of region/index (see isProjectedLine above).
        const metricValueDataset = {
            label: 'Metric value',
            data: trendData,
            borderColor: isProjectedLine ? PROJECTION_LINE_COLOR : '#EC4899', // fallback only — segment.borderColor below overrides per-segment for the real trend line
            // Each segment is colored by the point it STARTS from (ctx.p0DataIndex),
            // so a segment leaving a red point stays red until it reaches the next
            // point, then switches to THAT point's color — not a gradient, a hard
            // switch at each point, per the ask. The projection line skips all of
            // that and stays PROJECTION_LINE_COLOR throughout.
            segment: {
                borderColor: (segCtx: any) => (isProjectedLine ? PROJECTION_LINE_COLOR : (pointColors[segCtx.p0DataIndex] ?? '#EC4899')),
            },
            backgroundColor: 'transparent',
            borderWidth: 3,
            pointRadius: trendData.map(() => 6),
            pointBackgroundColor: pointColors,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            tension: 0.4,
            spanGaps: false,
        };

        // Create the chart
        chartInstanceRef.current = new ChartJS(ctx, {
            type: 'line',
            plugins: [labelsPlugin],
            data: {
                labels,
                datasets: [...goalDatasets, metricValueDataset],
            },
            options: {
                maintainAspectRatio: false,
                responsive: labels.length > 7,
                interaction: {
                    mode: 'nearest',
                    intersect: false,
                },
                layout: {
                    padding: {
                        // Reverted 70 -> 50: the wider gutter existed only to make
                        // room for the on-chart goal name+value labels removed
                        // above per requirement #7 — no longer needed.
                        left: 50,
                        right: 110,
                        top: 30, // Add padding for Current label
                        bottom: 10,
                    },
                },
                elements: {
                    point: {
                        hoverRadius: 6,
                        hitRadius: 10,
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        mode: 'nearest',
                        intersect: false,
                        callbacks: {
                            label(context: any) {
                                return context.dataset.label + ': ' + (CAPSession?.metricTypeName === 'Percentage' ? context.parsed.y + '%' : context.parsed.y);
                            },
                        },
                    },
                    datalabels: {
                        display: false,
                    },
                },
                scales: {
                    x: {
                        type: 'category',
                        grid: {
                            display: false,
                        },
                        ticks: {
                            font: {
                                size: 11,
                            },
                            maxRotation: 45, // Rotate labels if needed for better fit
                            minRotation: 0,
                            autoSkip: false, // CHANGED: Don't skip any labels
                            color: '#64748B',
                        },
                        border: {
                            display: false,
                        },
                    },
                    y: {
                        type: 'linear',
                        min: (() => {
                            const values = [
                                ...trendData.filter((v): v is number => v !== null),
                                ...goals.map((g) => g.value),
                                ...(CAPSession?.sessions ?? [])
                                    .map((s: any) => s.metricCurrentValueOverride != null
                                        ? CaluculateMetricValue(s.metricCurrentValueOverride, CAPSession?.metricTypeName)
                                        : null)
                                    .filter((v: any) => v != null),
                            ];
                            return Math.floor(Math.min(...values) - 10);
                        })(),
                        max: (() => {
                            const values = [
                                ...trendData.filter((v): v is number => v !== null),
                                ...goals.map((g) => g.value),
                                ...(CAPSession?.sessions ?? [])
                                    .map((s: any) => s.metricCurrentValueOverride != null
                                        ? CaluculateMetricValue(s.metricCurrentValueOverride, CAPSession?.metricTypeName)
                                        : null)
                                    .filter((v: any) => v != null),
                            ];
                            return Math.ceil(Math.max(...values) + 10);
                        })(),
                        ticks: {
                            stepSize: 10,
                            callback(value: any) {
                                return CAPSession?.metricTypeName === 'Percentage' ? value + '%' : value?.toString();
                            },
                            font: { size: 11 },
                            color: '#64748B',
                        },
                        grid: {
                            color: '#E5E7EB',
                        },
                        border: {
                            display: false,
                        },
                    },
                },
            },
        });

        // Cleanup
        return function cleanup() {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [CAPSession, goals, index]);

    const getPageTitle = (pageType: 'analyze' | 'action-plan' | 'follow-up') => {
        switch (pageType) {
            case 'follow-up':
                return 'Follow Up: Results';
            case 'action-plan':
                return 'Coach: Action Plan';
            case 'analyze':
                return 'Analyze: Observations';
            default:
                return '';
        }
    };

    function formatDateRange(startDate: string) {
        if (!startDate) return '--';
        const parseDate = (dateStr: string) => {
            const [datePart] = dateStr.split(' ');
            const [month, day, year] = datePart.split('/');
            return new Date(Number(year), Number(month) - 1, Number(day));
        };

        const start = parseDate(startDate);
        const startFormatted = `${monthNames[start.getMonth() + 1]} ${String(start.getDate()).padStart(2, '0')}, ${start.getFullYear()}`;

        let resolvedEndDate: Date;
        if (CAPSession?.coachingCompleteDate) {
            resolvedEndDate = parseDate(CAPSession.coachingCompleteDate);
        } else if (lastSession?.followUpDate) {
            resolvedEndDate = parseDate(lastSession.followUpDate);
        } else {
            resolvedEndDate = new Date(); // today
        }

        const endFormatted = `${monthNames[resolvedEndDate.getMonth() + 1]} ${String(resolvedEndDate.getDate()).padStart(2, '0')}, ${resolvedEndDate.getFullYear()}`;

        return `${startFormatted} - ${endFormatted}`;
    }

    return (
        <div id="coaching-glidepath">
            <div className="container">
                {/* Header */}

                <div className="header mb-5">
                    <h2 className="title">
                        {getPageTitle(pageType as 'analyze' | 'action-plan' | 'follow-up')}
                    </h2>
                    <p className="subtitle">
                        Document your observations after listening to calls, reviewing emails/chats, and
                        examining customer surveys.
                    </p>
                </div>

                <div className="content-glidepath">
                    {/* Left Side - Profile Info */}
                    <div className="profile">
                        <div className="profile__info">
                            {/* Avatar */}
                            <div className="avatar">{CAPSession?.shortNameAlt}</div>{' '}
                            <h3 className="name">
                                {(CAPSession?.employeeName ?? '').replace(/\(/, ' (')}
                            </h3>
                            <div className="info-item">
                                {' '}
                                <strong>Period:</strong>{' '}
                                {formatDateRange(CAPSession?.coachingStartDate)}
                            </div>
                            <div className="info-item">
                                <strong>Metric Coached:</strong> {CAPSession?.label}
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Chart */}
                    <div className="chart-section">
                        {/* Chart Legend Header */}
                        <div className="chart-header">
                            <div className="chart-legend" style={{ paddingLeft: '50px' }}>
                                {isMultiGoal ? (
                                    <>
                                        <div className="legend-item">
                                            <span className="legend-swatch" style={{ borderTopColor: meetsGoal?.lineColor ?? GOAL_COLORS.meets.lineColor }} />
                                            <span style={{ color: meetsGoal?.labelColor ?? GOAL_COLORS.meets.labelColor }}>Meets: {goals.find((g) => g.key === 'meets')?.displayValue ?? '--'}</span>
                                        </div>
                                        <div className="legend-item">
                                            <span className="legend-swatch" style={{ borderTopColor: exceedsGoal?.lineColor ?? GOAL_COLORS.exceeds.lineColor }} />
                                            <span style={{ color: exceedsGoal?.labelColor ?? GOAL_COLORS.exceeds.labelColor }}>Exceeds: {goals.find((g) => g.key === 'exceeds')?.displayValue ?? '--'}</span>
                                        </div>
                                    </>
                                ) : <div className="legend-item">
                                    <span className="legend-swatch" style={{ borderTopColor: meetsGoal?.lineColor ?? GOAL_COLORS.target.lineColor }} />
                                    <span style={{ color: meetsGoal?.labelColor ?? GOAL_COLORS.target.labelColor }}>Target: {goals.find((g) => g.key === 'target')?.displayValue ?? '--'}</span>
                                </div>}
                                <div className="legend-item">
                                    <span className="legend-dot" style={{ backgroundColor: SYSTEM_COLORS.overrideText }} />
                                    <span style={{ color: SYSTEM_COLORS.overrideText }}>Override</span>
                                </div>
                                <div className="legend-item">
                                    <span className="legend-dot" style={{ backgroundColor: currentPointColor }} />
                                    <span style={{ color: currentPointColor }}>Current</span>
                                </div>
                            </div>
                        </div>

                        {/* Chart */}
                        <div className="chart-container-glidepath">
                            <canvas ref={chartRef} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CoachingGlidePath;